// Config for the catalogue hubs, moved off /seo into the app.
//
// Each hub is the same shape — a list of entities with a title count — so one
// page renders them all.
//
// `endpoint` is the /hubs JSON API; the /seo pages keep their own rendering for
// crawlers and share the same underlying query.
//
// `detail` returns { to } for a native SPA route or { href } for a /seo page.
// Both remain: the app's title and person pages are keyed on the TMDB id, and
// the catalogue holds entities we've never matched to TMDB, so those have no
// native page to reach. They keep the server-rendered one rather than linking
// to a route that would 404.

export function slugify(name) {
  return (
    (name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 80) || 'entry'
  );
}

export const HUBS = {
  franchises: {
    path: '/franchises',
    label: 'Franchises',
    heading: 'Franchises',
    blurb: 'Film and TV franchises and collections — every title in order.',
    endpoint: '/hubs/franchises',
    unit: 'title',
    detail: (e) => ({ to: `/franchise/${e.id}` }),
  },
  genres: {
    path: '/genres',
    label: 'Genres',
    heading: 'Genres',
    blurb: 'Browse the catalogue by genre.',
    endpoint: '/hubs/genres',
    unit: 'title',
    // Genre pages key on the slug alone, not a uuid — the backend derives it
    // with the same rule, so this must stay in step with seo_routes._slugify.
    detail: (e) => ({ to: `/genre/${slugify(e.name)}` }),
  },
  studios: {
    path: '/studios',
    label: 'Studios',
    heading: 'Studios & streamers',
    blurb: 'Studios, streamers and distributors, by how much they carry.',
    endpoint: '/hubs/studios',
    unit: 'title',
    // CompanyDetail is keyed on our own uuid, so every studio has a native page.
    detail: (e) => ({ to: `/company/${e.id}` }),
  },
  people: {
    path: '/people',
    label: 'Cast & crew',
    heading: 'Cast & crew',
    blurb: 'Actors, directors and writers by number of credits.',
    endpoint: '/hubs/people',
    unit: 'credit',
    // PersonDetail fetches /person/{tmdb_id}, so someone we hold with no TMDB
    // id keeps the /seo page — the native route would 404 on our uuid.
    detail: (e) => (e.tmdb_id
      ? { to: `/person/${e.tmdb_id}` }
      : { href: `/seo/person/${slugify(e.name)}-${e.id}` }),
  },
};

export const HUB_LIST = Object.values(HUBS);

// The detail pages behind the franchise and studio-style hubs. Genres and
// franchises had no native page at all — only /seo — so these are what let the
// hub links stop leaving the app.
export const HUB_DETAILS = {
  franchise: {
    slug: 'franchise',
    param: 'id',
    noun: 'franchise',
    backTo: '/franchises',
    backLabel: 'All franchises',
    heading: (name) => `${name} — in order`,
    blurb: (name) => `Every film and TV show in the ${name} franchise, in release order.`,
    // Followable via the graph (backend VALID_FOLLOW_TYPES); genre is not —
    // it's a TMDB tag, not a graph node with its own id.
    entityType: 'franchise',
  },
  genre: {
    slug: 'genre',
    param: 'slug',
    noun: 'genre',
    backTo: '/genres',
    backLabel: 'All genres',
    heading: (name) => `${name} movies & TV`,
    blurb: (name) => `${name} films and series, most recently active first.`,
  },
};

// Where a title on a hub detail page goes. Same rule as people: the app's
// title pages are keyed on the TMDB id, so anything we hold without one falls
// back to its /seo page. Games have no native page at all yet.
export function titleTarget(t) {
  if (t.tmdb_id && t.media_type === 'tv') return { to: `/tv/${t.tmdb_id}` };
  if (t.tmdb_id && t.media_type === 'movie') return { to: `/movie/${t.tmdb_id}` };
  return { href: `/seo/title/${slugify(t.title)}-${t.id}` };
}
